import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from "@mui/material";

import { getCareGapTypeSummary } from "../../../../utils/services/CareGapsV2/careGapsV2Service";
import { getLocations, getPcps } from "../../../../utils/services/control/masterDataService";

const SegmentBar = ({ open, submitted, closed, total }) => {
  const safeTotal = total > 0 ? total : 1;
  const wOpen = (open / safeTotal) * 100;
  const wSubmitted = (submitted / safeTotal) * 100;
  const wClosed = (closed / safeTotal) * 100;
  return (
    <Box sx={{ display: "flex", height: 18, borderRadius: 1, overflow: "hidden", bgcolor: "#eee" }}>
      <Tooltip title={`Open: ${open}`}><Box sx={{ width: `${wOpen}%`, bgcolor: "#e53935" }} /></Tooltip>
      <Tooltip title={`Submitted: ${submitted}`}><Box sx={{ width: `${wSubmitted}%`, bgcolor: "#fb8c00" }} /></Tooltip>
      <Tooltip title={`Closed: ${closed}`}><Box sx={{ width: `${wClosed}%`, bgcolor: "#43a047" }} /></Tooltip>
    </Box>
  );
};

export default function ReportsTab({ measurementYear }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [pcpEmployeeId, setPcpEmployeeId] = useState("");
  const [includeAllRow, setIncludeAllRow] = useState(true);

  const [locations, setLocations] = useState([]);
  const [pcps, setPcps] = useState([]);

  useEffect(() => {
    Promise.all([
      getLocations({ status: 1, clinical: true }).catch(() => []),
      getPcps({ status: 1 }).catch(() => []),
    ]).then(([locs, pcpList]) => {
      setLocations(Array.isArray(locs) ? locs : []);
      setPcps(Array.isArray(pcpList) ? pcpList : []);
    });
  }, []);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCareGapTypeSummary({
        measurementYear: measurementYear || undefined,
        locationId: locationId || undefined,
        pcpEmployeeId: pcpEmployeeId || undefined,
        includeAllRow,
      });
      setRows(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error("Error loading type summary:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [measurementYear, locationId, pcpEmployeeId, includeAllRow]);

  useEffect(() => { fetch(); }, [fetch]);

  const { measureRows, allRow } = useMemo(() => {
    const all = rows.find((r) => r.careGapType === "All");
    const others = rows.filter((r) => r.careGapType !== "All");
    return { measureRows: others, allRow: all };
  }, [rows]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Location</InputLabel>
            <Select label="Location" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <MenuItem value=""><em>Whole organization</em></MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.locationId} value={l.locationId}>{l.medicalCenter}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>PCP</InputLabel>
            <Select label="PCP" value={pcpEmployeeId} onChange={(e) => setPcpEmployeeId(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              {pcps.map((p) => (
                <MenuItem key={p.employeePcpId} value={p.employeePcpId}>{p.pcpFullName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={includeAllRow} onChange={(e) => setIncludeAllRow(e.target.checked)} />}
            label="Include 'All' row"
          />

          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            <LegendDot color="#e53935" label="Open" />
            <LegendDot color="#fb8c00" label="Submitted" />
            <LegendDot color="#43a047" label="Closed" />
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : measureRows.length === 0 && !allRow ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">No data for the current filters.</Typography>
        </Paper>
      ) : (
        <>
          {allRow && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: "#fafafa" }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="body2" fontWeight={800}>All measures</Typography>
                  <Typography variant="caption" color="text.secondary">Total {allRow.total.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <SegmentBar open={allRow.open} submitted={allRow.submitted} closed={allRow.closed} total={allRow.total} />
                </Box>
                <Stack direction="row" spacing={2}>
                  <CountChip label="open" value={allRow.open} color="#e53935" />
                  <CountChip label="submitted" value={allRow.submitted} color="#fb8c00" />
                  <CountChip label="closed" value={allRow.closed} color="#43a047" />
                </Stack>
              </Stack>
            </Paper>
          )}

          <Stack spacing={1}>
            {measureRows.map((r) => (
              <Paper key={r.careGapType} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={1.5}>
                  <Box sx={{ minWidth: { md: 280 } }}>
                    <Typography variant="body2" fontWeight={700}>{r.careGapType}</Typography>
                    <Typography variant="caption" color="text.secondary">Total {r.total.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <SegmentBar open={r.open} submitted={r.submitted} closed={r.closed} total={r.total} />
                  </Box>
                  <Stack direction="row" spacing={1.5}>
                    <CountChip label="open" value={r.open} color="#e53935" />
                    <CountChip label="submitted" value={r.submitted} color="#fb8c00" />
                    <CountChip label="closed" value={r.closed} color="#43a047" />
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

const LegendDot = ({ color, label }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

const CountChip = ({ label, value, color }) => (
  <Box sx={{ textAlign: "center", minWidth: 60 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={800} sx={{ color }}>{value.toLocaleString()}</Typography>
  </Box>
);
